import type { WebAPIError } from '@/store/api/WebApiResponse';
import type { SerializedError } from '@reduxjs/toolkit';
import type { JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TranslateWebAPIError } from './TranslateWebAPIError';
import { VStack } from '../sgBootstrapReactWrapper/VStack';
import { Icon } from '../sgBootstrapReactWrapper/Icon';
import { Button } from '../sgBootstrapReactWrapper';

type Props = {
  title?: string;
  message?: string;
  error?: WebAPIError | SerializedError;
  onRequestRetry?: () => void;
  fullHeight?: boolean;
};

const LoadingErrorPanel = (props: Props) => {
  const { $t } = useIntl();

  const { title, message, error, onRequestRetry, fullHeight = true } = props;

  let displayedTitle: string;
  let displayedMessage: JSX.Element | string;

  if (error) {
    if (!('type' in error)) {
      // SerializedError : an error that was caught by rtk query while executing app code or rtk code
      console.error('Serialized error caught', error);
      displayedTitle = $t({ id: 'loadError.defaultTitle' });
      displayedMessage = $t({ id: 'global.frontendFailures.crash' });
    } else {
      displayedTitle = title ?? $t({ id: 'loadError.defaultTitle' });
      displayedMessage = message ?? <TranslateWebAPIError error={error} />;
    }
  } else {
    displayedTitle = title ?? $t({ id: 'loadError.defaultTitle' });
    displayedMessage = message ?? $t({ id: 'loadError.defaultMessage' });
  }

  return (
    <VStack
      fullHeight={fullHeight}
      verticalAlignment="center"
      horizontalAlignment="center"
      className="m-48px text-secondary"
    >
      <Icon icon="error_outline" size="xl" />
      <h2 className="mb-12px">{displayedTitle}</h2>
      <p data-e2e="loading-error">{displayedMessage}</p>
      {onRequestRetry && (
        <Button purpose="discreet" className="mt-48px" onClick={onRequestRetry}>
          <FormattedMessage id="tryAgain" />
        </Button>
      )}
    </VStack>
  );
};

export default LoadingErrorPanel;
